/**
 * ============================================================================
 * SESSION STORE
 * ============================================================================
 *
 * Everything the user changes during a visit: shell preferences, filters, and
 * mutable copies of the three collections agents write back to.
 *
 * NOTHING IS PERSISTED. No localStorage, no sessionStorage, no cookies. A
 * reload restores the seed exactly, which is what makes the build safe to
 * demonstrate with client-named data and is also why the footer says the data
 * is illustrative. When the API lands, these writes become mutations and this
 * store shrinks to shell preferences only.
 *
 * The store is hydrated once, from the fixtures behind the data adapter, by
 * `SessionBootstrap`. It never imports an adapter itself.
 */

import { create } from 'zustand';
import type {
  ActivationReport,
  ActivationSection,
  Analysis,
  ExternalReport,
  SrConfiguration,
  SrPartner,
} from '@/data/contracts';
import { reportCompleteness } from '@/domain/activation/model';
import { srCompute } from '@/domain/sr/model';
import type { Currency } from '@/lib/format';
import { CURRENCIES, nf, signedPct, todayStr } from '@/lib/format';
import { clamp } from '@/lib/math';
import { uid } from '@/lib/random';

export const DATE_RANGES = [
  'Season 2024/25',
  'Season 2023/24',
  'Last 12 months',
  'Contract to date',
] as const;
export type DateRange = (typeof DATE_RANGES)[number];

export type LockTab = 'reports' | 'agents' | 'library' | 'external';

export interface LibraryFilters {
  readonly brand: string;
  readonly partner: string;
  readonly status: string;
  readonly pillar: string;
}

const EMPTY_FILTERS: LibraryFilters = { brand: '', partner: '', status: '', pillar: '' };

export interface LockState {
  readonly tab: LockTab;
  readonly q: string;
  readonly module: string | null;
  readonly category: string | null;
}

interface SessionState {
  /* ---- shell ---- */
  readonly currency: Currency;
  readonly dateRange: DateRange;
  readonly search: string;
  readonly filters: LibraryFilters;
  readonly filterCount: number;
  readonly lock: LockState;

  /* ---- collections ---- */
  readonly analyses: readonly Analysis[];
  readonly externalReports: readonly ExternalReport[];
  readonly activationReports: readonly ActivationReport[];
  readonly hydrated: boolean;

  /* ---- shell actions ---- */
  setCurrency: (currency: Currency) => void;
  cycleCurrency: () => void;
  setDateRange: (range: DateRange) => void;
  cycleDateRange: () => void;
  setSearch: (value: string) => void;

  setFilters: (filters: Partial<LibraryFilters>) => void;
  applyFilters: () => number;
  clearFilters: () => void;

  setLock: (patch: Partial<LockState>) => void;
  lockTo: (tab: LockTab) => void;
  clearLibraryFilters: () => void;

  /* ---- collection actions ---- */
  hydrate: (payload: HydratePayload) => void;
  saveAnalysis: (analysis: Analysis) => void;
  duplicateAnalysis: (id: string) => Analysis | null;
  archiveAnalysis: (id: string) => void;

  addExternalReport: (report: ExternalReport) => void;

  saveActivationReport: (report: ActivationReport) => void;
  duplicateActivationReport: (id: string) => ActivationReport | null;
  patchActivationReport: (id: string, patch: Partial<ActivationReport>) => void;

  reset: () => void;
}

export interface HydratePayload {
  readonly analyses: readonly Analysis[];
  readonly externalReports: readonly ExternalReport[];
  readonly activationReports: readonly ActivationReport[];
  readonly srConfig: SrConfiguration;
  readonly srPartners: readonly SrPartner[];
  readonly sections: readonly ActivationSection[];
}

/**
 * Recomputes the derived KPIs on seeded rows so the library agrees with the
 * agents that own the arithmetic.
 *
 * Strategic Relevance rows get OpportunityScore / BaseScore / discount factor
 * from `srCompute`; activation rows get their completeness from
 * `reportCompleteness`. Neither figure is stored in the fixtures, because a
 * stored copy is a copy that can drift — which is exactly what happened in 2.0.
 */
function deriveSeedKpis(payload: HydratePayload): readonly Analysis[] {
  const { analyses, srConfig, srPartners, activationReports, sections } = payload;

  return analyses.map((analysis) => {
    if (analysis.moduleId === 'strategic-relevance' && analysis.ref?.srPartner) {
      const partner = srPartners.find((p) => p.id === analysis.ref?.srPartner);
      if (!partner || analysis.status !== 'complete') return analysis;
      const r = srCompute(partner, srConfig);
      return {
        ...analysis,
        confidence: clamp(Math.round(r.opp), 0, 100),
        kpis: [
          { label: 'OpportunityScore', value: nf(r.opp, 1) },
          { label: 'BaseScore', value: nf(r.base, 1) },
          { label: 'Discount factor', value: signedPct(r.df * 100) },
        ],
      };
    }

    if (analysis.moduleId === 'activation-report' && analysis.ref?.activation) {
      const report = activationReports.find((r) => r.id === analysis.ref?.activation);
      if (!report) return analysis;
      return {
        ...analysis,
        kpis: [
          ...analysis.kpis,
          { label: 'Completeness', value: `${reportCompleteness(report, sections)}%` },
        ],
      };
    }

    return analysis;
  });
}

const INITIAL = {
  currency: 'EUR' as Currency,
  dateRange: DATE_RANGES[0],
  search: '',
  filters: EMPTY_FILTERS,
  // The prototype opened with two filters shown as active in the top bar.
  filterCount: 2,
  lock: { tab: 'reports' as LockTab, q: '', module: null, category: null },
};

export const useSessionStore = create<SessionState>()((set, get) => ({
  ...INITIAL,
  analyses: [],
  externalReports: [],
  activationReports: [],
  hydrated: false,

  /* ---------------------------------------------------------------- shell -- */

  setCurrency: (currency) => {
    set({ currency });
  },
  cycleCurrency: () => {
    const order = CURRENCIES;
    const next = order[(order.indexOf(get().currency) + 1) % order.length];
    if (next !== undefined) set({ currency: next });
  },
  setDateRange: (dateRange) => {
    set({ dateRange });
  },
  cycleDateRange: () => {
    const i = DATE_RANGES.indexOf(get().dateRange);
    const next = DATE_RANGES[(i + 1) % DATE_RANGES.length];
    if (next !== undefined) set({ dateRange: next });
  },
  setSearch: (search) => {
    set({ search });
  },

  setFilters: (patch) => {
    set((s) => ({ filters: { ...s.filters, ...patch } }));
  },
  applyFilters: () => {
    const count = Object.values(get().filters).filter(Boolean).length;
    set({ filterCount: count });
    return count;
  },
  clearFilters: () => {
    set({ filters: EMPTY_FILTERS, filterCount: 0 });
  },

  setLock: (patch) => {
    set((s) => ({ lock: { ...s.lock, ...patch } }));
  },
  lockTo: (tab) => {
    set((s) => ({ lock: { ...s.lock, tab, module: null } }));
  },
  clearLibraryFilters: () => {
    set((s) => ({
      lock: { ...s.lock, q: '', module: null, category: null },
      search: '',
      filters: EMPTY_FILTERS,
      filterCount: 0,
    }));
  },

  /* ---------------------------------------------------------- collections -- */

  hydrate: (payload) => {
    set({
      analyses: deriveSeedKpis(payload),
      externalReports: [...payload.externalReports],
      // Deep-copied: the builder mutates `filled` and `manualInputs`, and the
      // fixtures must stay pristine for a reset.
      activationReports: payload.activationReports.map((r) => structuredClone(r)),
      hydrated: true,
    });
  },

  saveAnalysis: (analysis) => {
    set((s) => {
      const i = s.analyses.findIndex((a) => a.id === analysis.id);
      if (i >= 0) {
        const next = [...s.analyses];
        next[i] = analysis;
        return { analyses: next };
      }
      return { analyses: [analysis, ...s.analyses] };
    });
  },

  duplicateAnalysis: (id) => {
    const source = get().analyses.find((a) => a.id === id);
    if (!source) return null;
    const copy: Analysis = {
      ...source,
      id: uid('analysis'),
      name: `${source.name} (copy)`,
      status: 'draft',
      createdDate: todayStr(),
      lastModified: 'just now',
      kpis: [...source.kpis],
    };
    // A copy is a fresh draft: the confidence belonged to the original run.
    delete (copy as { confidence?: number }).confidence;
    set((s) => ({ analyses: [copy, ...s.analyses] }));
    return copy;
  },

  archiveAnalysis: (id) => {
    set((s) => ({ analyses: s.analyses.filter((a) => a.id !== id) }));
  },

  addExternalReport: (report) => {
    set((s) => ({ externalReports: [report, ...s.externalReports] }));
  },

  saveActivationReport: (report) => {
    set((s) => {
      const i = s.activationReports.findIndex((r) => r.id === report.id);
      if (i >= 0) {
        const next = [...s.activationReports];
        next[i] = report;
        return { activationReports: next };
      }
      return { activationReports: [report, ...s.activationReports] };
    });
  },

  duplicateActivationReport: (id) => {
    const source = get().activationReports.find((r) => r.id === id);
    if (!source) return null;
    const copy = structuredClone(source);
    const next: ActivationReport = {
      ...copy,
      id: uid('ar'),
      title: `${source.title} (copy)`,
      status: 'Draft',
      complete: false,
      lastModified: new Date().toISOString().slice(0, 10),
    };
    set((s) => ({ activationReports: [next, ...s.activationReports] }));
    return next;
  },

  patchActivationReport: (id, patch) => {
    set((s) => ({
      activationReports: s.activationReports.map((r) => (r.id === id ? { ...r, ...patch } : r)),
    }));
  },

  reset: () => {
    set({ ...INITIAL, hydrated: false });
  },
}));

/* ------------------------------------------------------------- selectors -- */

export const useCurrency = (): Currency => useSessionStore((s) => s.currency);
export const useDateRange = (): DateRange => useSessionStore((s) => s.dateRange);
export const useAnalyses = (): readonly Analysis[] => useSessionStore((s) => s.analyses);
export const useExternalReports = (): readonly ExternalReport[] =>
  useSessionStore((s) => s.externalReports);
export const useActivationReports = (): readonly ActivationReport[] =>
  useSessionStore((s) => s.activationReports);
